/**
 * The signed-in admin account that in-memory, per-operator UI state belongs to.
 *
 * Sign-out in the admin app is SPA-only (no page reload), so module-level state survives an account switch in the
 * same tab. State that is owned by an operator (unsaved editor drafts, for example) registers here to be dropped when
 * the account changes and to refuse being written on behalf of an account that is no longer signed in.
 * Nothing here is persisted and only the opaque account id is held.
 */
type Listener = (account: string | null) => void;

let active: string | null = null;
const listeners = new Set<Listener>();

export const currentAccount = (): string | null => active;

/** Called by the auth layer whenever the signed-in user is (re)resolved. A no-op while the account is unchanged. */
export function setActiveAccount(account: string | null | undefined) {
  const next = account ?? null;
  if (next === active) return;
  active = next;
  listeners.forEach((listener) => listener(next));
}

/** Runs `listener` after every account change. Returns the unsubscribe function. */
export function onAccountChange(listener: Listener) {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}
